import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import { IOrderByCondition } from 'Views/Components/ModelCollection/ModelQuery';
import * as Models from 'Models/Entities';

export interface ILocationEntityAttributes extends IModelAttributes {
	name: string;
	shortName: string;
	timezone: string;
	address: string;
	longitude: number;
	latitude: number;
	disabled: boolean;
	helmId: string;

	departureRoutes: Array<Models.RouteEntity | Models.IRouteEntityAttributes>;
	destinationRoutes: Array<Models.RouteEntity | Models.IRouteEntityAttributes>;
	stops: Array<Models.StopEntity | Models.IStopEntityAttributes>;
}
@entity('LocationEntity', 'Location')
export default class LocationEntity extends Model implements ILocationEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * The default order by field when the collection is loaded .
	 */
	public get orderByField(): IOrderByCondition<Model> | undefined {
		return {
			path: 'name',
			descending: false,
		};
	}

	@observable
	@attribute<LocationEntity, string>()
	public name: string;

	@observable
	@attribute<LocationEntity, string>()
	public shortName: string;

	/**
	 * The timezone of the location
	 */
	@observable
	@attribute<LocationEntity, string>()
	public timezone: string;

	/**
	 * The address of the locations port or office.
	 */
	@observable
	@attribute<LocationEntity, string>()
	public address: string;

	@observable
	@attribute<LocationEntity, number>()
	public longitude: number;

	@observable
	@attribute<LocationEntity, number>()
	public latitude: number;

	@observable
	@attribute<LocationEntity, boolean>()
	public disabled: boolean;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public departureRoutes: Models.RouteEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public destinationRoutes: Models.RouteEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public stops: Models.StopEntity[] = [];

	@observable
	@attribute<LocationEntity, string>()
	public helmId: string;

	constructor(attributes?: Partial<ILocationEntityAttributes>) {
		super(attributes);

		this.departureRoutes = [];
		this.destinationRoutes = [];
		this.stops = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<ILocationEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.shortName !== undefined) {
				this.shortName = attributes.shortName;
			}
			if (attributes.timezone !== undefined) {
				this.timezone = attributes.timezone;
			}
			if (attributes.address !== undefined) {
				this.address = attributes.address;
			}
			if (attributes.longitude !== undefined) {
				this.longitude = attributes.longitude;
			}
			if (attributes.latitude !== undefined) {
				this.latitude = attributes.latitude;
			}
			if (attributes.disabled !== undefined) {
				this.disabled = attributes.disabled;
			}
			if (attributes.helmId !== undefined) {
				this.helmId = attributes.helmId;
			}
			if (attributes.departureRoutes !== undefined && Array.isArray(attributes.departureRoutes)) {
				if (!this.departureRoutes) {
					this.departureRoutes = [];
				}
				for (const model of attributes.departureRoutes) {
					if (model instanceof Models.RouteEntity) {
						this.departureRoutes.push(model);
					} else {
						this.departureRoutes.push(new Models.RouteEntity(model));
					}
				}
			}
			if (attributes.destinationRoutes !== undefined && Array.isArray(attributes.destinationRoutes)) {
				if (!this.destinationRoutes) {
					this.destinationRoutes = [];
				}
				for (const model of attributes.destinationRoutes) {
					if (model instanceof Models.RouteEntity) {
						this.destinationRoutes.push(model);
					} else {
						this.destinationRoutes.push(new Models.RouteEntity(model));
					}
				}
			}
			if (attributes.stops !== undefined && Array.isArray(attributes.stops)) {
				if (!this.stops) {
					this.stops = [];
				}
				for (const model of attributes.stops) {
					if (model instanceof Models.StopEntity) {
						this.stops.push(model);
					} else {
						this.stops.push(new Models.StopEntity(model));
					}
				}
			}
		}
	}
}
